import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddFuelTypeMutation, useGetCategoriesQuery, useUpdateFuelTypeMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  fuelTypeData?: any;
}

export default function AddFuelType({
  open,
  onClose,
  setOpen,
  fuelTypeData
}: ModalProps) {

  // Queries
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const { data: categoriesData } = useGetCategoriesQuery(
    { page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addFuelType, { isLoading: isAdding }] = useAddFuelTypeMutation();
  const [updateFuelType, { isLoading: isUpdating }] = useUpdateFuelTypeMutation();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (fuelTypeData && open) {
      setName(fuelTypeData.name);
      let catIds: string[] = [];
      if (fuelTypeData.category && Array.isArray(fuelTypeData.category) && fuelTypeData.category.length > 0) {
        catIds = fuelTypeData.category.map((c: any) => c._id);
      } else if (fuelTypeData.categoryId) {
        catIds = Array.isArray(fuelTypeData.categoryId) ? fuelTypeData.categoryId : [fuelTypeData.categoryId];
      }
      setSelectedCategories(catIds);
      const vType = fuelTypeData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!fuelTypeData && open) {
      // Reset form
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [fuelTypeData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(value);
    }

    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Name is required / الاسم مطلوب";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "Category is required / الفئة مطلوبة";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    const payload = {
      name,
      categoryId: selectedCategories,
      type: Number(selectedVehicleType),
    };

    try {
      if (fuelTypeData?._id) {
        await updateFuelType({ id: fuelTypeData._id, ...payload }).unwrap();
        toast.success("Fuel Type updated successfully / تم تحديث نوع الوقود بنجاح");
      } else {
        await addFuelType(payload).unwrap();
        toast.success("Fuel Type added successfully / تمت إضافة نوع الوقود بنجاح");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong / حدث خطأ ما");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{fuelTypeData ? "Edit Fuel Type / تعديل نوع الوقود" : "Add Fuel Type / إضافة نوع وقود"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group">
              <label htmlFor="fuel-type-name">Fuel Type Name / اسم نوع الوقود</label>
              <TextField
                id="fuel-type-name"
                placeholder="Enter Name / أدخل الاسم"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category / الفئة</label>
              <Select
                fullWidth
                hiddenLabel
                multiple
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  if (selected.length === 0) return "Select Category / اختر الفئة";
                  const selectedNames = categoriesData?.data?.data
                    ?.filter((cat: any) => selected.includes(cat._id))
                    ?.map((cat: any) => cat.name);
                  return selectedNames?.join(", ");
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
                disabled={!selectedVehicleType}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>
                  {selectedVehicleType ? "Select Category / اختر الفئة" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                </MenuItem>
                {categoriesData?.data?.data?.length > 0 && (
                  <MenuItem value="all">
                    <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                  </MenuItem>
                )}
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={selectedCategories.includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
